import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, TrendingUp, FileText, ArrowRight, Check, Users, ClipboardCheck,
  IndianRupee, Globe, Scale, Landmark, Calculator, Building, BarChart3, BookOpen, Sparkles,
} from 'lucide-react';

/* Three service pillars — mirrors the landing-page story */
const pillars = [
  {
    key: 'due-diligence',
    icon: ShieldCheck,
    overline: 'Pillar 01',
    title: 'Due Diligence Assistance',
    tagline: 'Investor-grade diligence, done right',
    desc: 'We prepare your books, cap table and data room to survive institutional scrutiny — for M&A, VC rounds, and legal audits. Backed by Chartered Accountants and IBBI-registered valuers.',
    dark: true,
    offerings: [
      { icon: ShieldCheck, title: 'Audit & Assurance', items: ['Statutory & tax audit', 'Internal & concurrent audit', 'GST audit & reconciliation', 'Forensic & stock audit'] },
      { icon: Scale, title: 'Registered Valuation', items: ['Equity & ESOP valuation (Rule 11UA)', 'Business valuation for M&A', 'Intangible & IP valuation', 'Fair value under Companies Act'] },
      { icon: ClipboardCheck, title: 'Diligence Readiness', items: ['Data room preparation', 'Financial & tax diligence', 'Cap table clean-up', 'Red-flag remediation'] },
    ],
  },
  {
    key: 'funding',
    icon: TrendingUp,
    overline: 'Pillar 02',
    title: 'Startup Funding Consultancy',
    tagline: 'From pitch deck to term sheet',
    desc: 'Strategic fundraising support from a team that sits on your side of the table — financial models, valuations, investor readiness and SEBI-registered merchant banking advisory.',
    dark: false,
    offerings: [
      { icon: TrendingUp, title: 'Fundraise Advisory', items: ['Financial modelling & projections', 'Pitch-deck financials', 'Valuation & benchmarking', 'Term-sheet advisory'] },
      { icon: Landmark, title: 'Merchant Banking', items: ['SEBI & merchant-banking advisory', 'Capital structuring', 'ESOP scheme design', 'Investor relations support'] },
      { icon: Globe, title: 'Structuring & FEMA', items: ['Entity & holding structuring', 'FEMA / cross-border compliance', 'DTAA & cross-border taxation', 'Startup India & 80IAC benefits'] },
    ],
  },
  {
    key: 'compliance',
    icon: FileText,
    overline: 'Pillar 03',
    title: 'Full-Stack Compliance Services',
    tagline: 'One team, one portal, zero missed deadlines',
    desc: 'End-to-end management of your entire compliance stack — accounting, GST, TDS, income tax, ROC and secretarial — all tracked in your Growartha client portal.',
    dark: false,
    offerings: [
      { icon: FileText, title: 'GST', items: ['GSTR-1 & GSTR-3B filing', 'Annual GSTR-9 / 9C', 'ITC reconciliation', 'Notices & registrations'] },
      { icon: Calculator, title: 'Income Tax', items: ['ITR-1 to ITR-7 filing', 'Advance tax & planning', 'Capital gains computation', 'Assessments & appeals'] },
      { icon: BarChart3, title: 'TDS', items: ['24Q / 26Q / 27Q filing', 'Form 16 / 16A generation', 'Form 26AS / AIS matching', 'Correction returns'] },
      { icon: Building, title: 'ROC & Secretarial', items: ['AOC-4 & MGT-7 filing', 'Director KYC & DIN', 'Board resolutions & minutes', 'Secretarial audit (S.204)'] },
      { icon: BookOpen, title: 'Accounting & MIS', items: ['Book-keeping & ledgers', 'Monthly MIS reporting', 'Payroll & reconciliations', 'Virtual CFO support'] },
      { icon: Users, title: 'Advisory', items: ['Tax planning & structuring', 'MSME & scheme advisory', 'Founder compensation', 'Compliance health checks'] },
    ],
  },
];

const ServicesPage = () => {
  const navigate = useNavigate();
  const go = () => navigate('/#contact');

  return (
    <div className="bg-[#FDFBF7]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#042F2E] pt-32 pb-16 lg:pt-40 lg:pb-20" data-testid="services-hero">
        <div className="pointer-events-none absolute -right-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-[#14B8A6]/15 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="container-grid relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#5eead4] font-body">
              <Sparkles className="h-3.5 w-3.5" /> Our Services
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white font-heading leading-[1.08]" data-testid="services-heading">
              Three pillars. <span className="text-[#14B8A6] italic">Every</span> financial need covered.
            </h1>
            <p className="mt-5 max-w-xl text-base lg:text-lg text-white/70 leading-relaxed font-body">
              Due diligence, startup funding and full-stack compliance — delivered by one team of CAs, CS, CMAs, Registered Valuers and SEBI-registered Merchant Bankers.
            </p>
            <Button onClick={go} className="mt-8 group bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] font-semibold rounded-full px-8 h-12 text-sm transition-colors" data-testid="services-contact-cta">
              Book a Free Consultation <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14 pt-10 border-t border-white/10">
            {[
              { icon: Users, value: '100+', label: 'Startups Served' },
              { icon: ClipboardCheck, value: '10k+', label: 'Filings Done' },
              { icon: IndianRupee, value: '₹100Cr+', label: 'Funds Raised' },
              { icon: Globe, value: '15+', label: 'Years Experience' },
            ].map((s) => (
              <div key={s.label} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/[0.06] flex items-center justify-center flex-shrink-0"><s.icon className="h-4 w-4 text-[#14B8A6]" /></div>
                <div>
                  <p className="text-xl font-semibold text-white font-heading">{s.value}</p>
                  <p className="text-xs text-white/40 mt-0.5 font-body">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      {pillars.map((p, idx) => (
        <section key={p.key} id={p.key} className={`py-20 lg:py-24 ${idx % 2 === 0 ? 'bg-[#FDFBF7]' : 'bg-[#F3F0EA]'}`} data-testid={`services-pillar-${p.key}`}>
          <div className="container-grid">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              {/* Pillar intro */}
              <div className="lg:col-span-4 lg:sticky lg:top-28">
                <p className="text-xs font-bold tracking-[0.22em] uppercase font-body text-[#0f766e]">{p.overline}</p>
                <div className="mt-4 h-14 w-14 rounded-xl bg-[#042F2E] flex items-center justify-center">
                  <p.icon className="h-7 w-7 text-[#5eead4]" />
                </div>
                <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-heading leading-tight">{p.title}</h2>
                <p className="mt-2 text-sm font-semibold text-[#0f766e] font-body">{p.tagline}</p>
                <p className="mt-4 text-base text-muted-foreground font-body leading-relaxed">{p.desc}</p>
                <Button onClick={go} className="mt-6 bg-[#042F2E] hover:bg-[#042F2E]/90 text-white rounded-full h-11 px-7 font-body font-semibold text-sm transition-colors" data-testid={`pillar-cta-${p.key}`}>
                  Talk to an expert <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Offerings */}
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                {p.offerings.map((o) => (
                  <div key={o.title} className="rounded-xl bg-white border border-[#E7E5E4] p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="h-10 w-10 rounded-lg bg-[#14B8A6]/10 flex items-center justify-center">
                      <o.icon className="h-5 w-5 text-[#0f766e]" />
                    </div>
                    <h3 className="mt-4 text-lg font-medium font-heading tracking-tight text-foreground">{o.title}</h3>
                    <ul className="mt-3 space-y-2">
                      {o.items.map((it) => (
                        <li key={it} className="flex items-start gap-2.5 text-sm text-foreground/75 font-body leading-snug">
                          <Check className="h-4 w-4 text-[#14B8A6] mt-0.5 flex-shrink-0" /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-20 lg:py-24 bg-[#042F2E] relative overflow-hidden" data-testid="services-bottom-cta">
        <div className="pointer-events-none absolute -left-20 -bottom-20 h-80 w-80 rounded-full bg-[#14B8A6]/10 blur-3xl" />
        <div className="container-grid text-center relative">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white font-heading leading-tight">
            One firm for your entire financial journey.
          </h2>
          <p className="mt-4 text-base text-white/60 font-body max-w-xl mx-auto">
            Share your requirements and we'll prepare a custom proposal — including equity-friendly models for early-stage startups.
          </p>
          <Button onClick={go} className="mt-8 group bg-[#14B8A6] hover:bg-[#0fa093] text-[#042F2E] font-semibold rounded-full px-10 h-12 text-sm transition-colors" data-testid="services-final-cta">
            Book a Free Consultation <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
